/**
 * Adapter: Support-Inbox message → canonical `Message` (design doc §3.7, adapter 1).
 *
 * The global compose modal (`MODAL_IDS.messages.compose`) threads a reply from an
 * `originalMessage: Message`. It reads that message for the reply recipient
 * (`sender.email`), the `Re:` subject, the quoted body, and — on the forward path —
 * `providerMessageId` for the RFC threading headers. A live support-inbox message
 * carries a different shape (`from`/`to` raw headers, `rfcMessageId`, `receivedAt`),
 * so we map it onto `Message` here. Keeping the adapter in the feature folder keeps
 * the compose system untouched.
 */

import type { Message, Sender, Recipient } from "@/lib/core/crm/types";
import { hasVisibleAttachments } from "./attachment-visibility";
import type {
  SupportInboxListMessage,
  SupportInboxThreadMessage,
} from "./types";

/** Addresses at this domain are internal (WTC staff or the support group itself). */
const INTERNAL_EMAIL_DOMAIN = "westtn.consulting";

function isInternalAddress(email: string, groupEmail?: string | null): boolean {
  const normalized = email.trim().toLowerCase();
  if (!normalized) return true;
  if (groupEmail && normalized === groupEmail.trim().toLowerCase()) return true;
  return normalized.endsWith(`@${INTERNAL_EMAIL_DOMAIN}`);
}

/**
 * Parse an RFC 5322 address header fragment (`"Jane Doe <jane@x.com>"`, `"<a@b>"`,
 * or a bare `a@b`) into a display name + email. Falls back gracefully — the email
 * is what matters for reply routing; the name is cosmetic.
 */
function parseAddress(raw: string): { name: string; email: string } {
  const trimmed = (raw ?? "").trim();
  if (!trimmed) return { name: "", email: "" };

  const angle = trimmed.match(/^(.*)<([^>]+)>\s*$/);
  if (angle) {
    const name = angle[1].trim().replace(/^"(.*)"$/, "$1").trim();
    const email = angle[2].trim();
    return { name: name || email, email };
  }

  return { name: trimmed, email: trimmed };
}

/** Split a `To` header on commas that are not inside quotes, then parse each. */
function parseRecipients(raw: string): Recipient[] {
  const trimmed = (raw ?? "").trim();
  if (!trimmed) return [];

  const parts: string[] = [];
  let current = "";
  let inQuotes = false;
  for (const char of trimmed) {
    if (char === '"') inQuotes = !inQuotes;
    if (char === "," && !inQuotes) {
      parts.push(current);
      current = "";
    } else {
      current += char;
    }
  }
  if (current.trim()) parts.push(current);

  return parts
    .map((part) => parseAddress(part))
    .filter((addr) => addr.email.length > 0)
    .map((addr) => ({ name: addr.name || addr.email, email: addr.email }));
}

/** First external (non-group, non-WTC) address parsed from a raw header, or null. */
function firstExternalIn(
  rawHeader: string | null | undefined,
  groupEmail?: string | null,
): Sender | null {
  if (!rawHeader) return null;
  for (const addr of parseRecipients(rawHeader)) {
    if (addr.email && !isInternalAddress(addr.email, groupEmail)) {
      return { name: addr.name || addr.email, email: addr.email };
    }
  }
  return null;
}

/**
 * Resolve the EXTERNAL counterparty of a support thread — the customer to reply
 * to, never the support group itself or a WTC staffer. A support thread mixes the
 * original sender's inbound mail with WTC's outbound replies (all
 * `@westtn.consulting`), so replying to whichever message was clicked can wrongly
 * target the group.
 *
 * Priority, newest message first:
 *   1. `Reply-To` — the RFC 5322 intended reply target. Mailing-list / "via"
 *      mail (e.g. `TechSoup ... via hopewellwatersupport@westtn.consulting`) sets
 *      `from` to the GROUP and puts the real correspondent ONLY in `Reply-To`
 *      (`customerservice@techsoup.org`). Honoring it is not optional.
 *   2. `from` — a customer who mailed the group directly (no list rewrite).
 *   3. `to` — the customer may only appear as a recipient on WTC-authored replies.
 *
 * Returns null when no external address is found (all-internal thread) — the
 * caller then falls back to the raw message sender.
 */
export function resolveExternalCounterparty(
  messages: Array<SupportInboxListMessage | SupportInboxThreadMessage>,
  groupEmail?: string | null,
): Sender | null {
  const newestFirst = [...messages].sort(
    (a, b) => Date.parse(b.receivedAt) - Date.parse(a.receivedAt),
  );

  // Reply-To first (across all messages), then From — both identify the party a
  // reply should reach; Reply-To wins the "via" trap. Newest message wins ties.
  for (const msg of newestFirst) {
    const viaReplyTo = firstExternalIn(msg.replyTo, groupEmail);
    if (viaReplyTo) return viaReplyTo;
    const viaFrom = firstExternalIn(msg.from, groupEmail);
    if (viaFrom) return viaFrom;
  }

  // Fallback: the first external recipient in any `to` header (the original
  // customer may only appear as a recipient on WTC-authored messages).
  for (const msg of newestFirst) {
    const viaTo = firstExternalIn(msg.to, groupEmail);
    if (viaTo) return viaTo;
  }

  return null;
}

/**
 * Build a `Message` from a support-inbox message. Accepts either the list shape
 * (snippet only) or the thread shape (full bodies); the quoted reply body prefers
 * text content, then the stripped snippet.
 */
export function supportInboxMessageToMessage(
  msg: SupportInboxListMessage | SupportInboxThreadMessage,
): Message {
  const sender: Sender = (() => {
    const parsed = parseAddress(msg.from);
    return { name: parsed.name || parsed.email, email: parsed.email };
  })();

  const recipients = parseRecipients(msg.to);

  // Carry the Reply-To header onto the canonical Message so the compose modal's
  // reply-recipient resolver (`extractReplyEmail`) prefers it over `sender.email`
  // — the two-part contract of EMAIL_REPLY_TARGETS_REPLY_TO. Empty → undefined so
  // the resolver cleanly falls through to sender.
  const replyTo = parseRecipients(msg.replyTo ?? "");

  const threadMsg = msg as Partial<SupportInboxThreadMessage>;
  const quotedBody =
    threadMsg.textContent ?? threadMsg.htmlContent ?? msg.snippet ?? "";

  return {
    id: msg.messageId,
    subject: msg.subject,
    body: quotedBody,
    bodyPreview: msg.snippet ?? "",
    sender,
    recipients,
    replyTo: replyTo.length > 0 ? replyTo : undefined,
    receivedDateTime: msg.receivedAt,
    sentDateTime: msg.receivedAt,
    isRead: true,
    // From the message, never a literal — and through the SAME predicate the
    // reading pane and the thread-list paperclip already share, so a message
    // never advertises a clip that opens to nothing. (An inline image is a
    // signature logo; an inline non-image is a real document that happened to
    // arrive marked `Content-Disposition: inline`.) `attachments` is optional on
    // the wire — a response cached from a build predating attachment support
    // omits it, which reads as "none listed" rather than crashing.
    hasAttachments: hasVisibleAttachments(msg.attachments),
    importance: "normal",
    // Gmail threadId — lets the compose modal group the reply with the thread.
    conversationId: msg.threadId,
    provider: "google-mail",
    // Prefer the global RFC Message-ID so In-Reply-To / References resolve
    // across mailboxes; fall back to the per-mailbox Gmail message id.
    providerMessageId: msg.rfcMessageId ?? msg.messageId,
    // No Hub inbox owns this ephemeral live-read message.
    inboxId: "",
  };
}
